<template>
  <iframe
    ref="frame"
    class="ae-d3-insight"
    :style="{ height: height + 'px' }"
    sandbox="allow-scripts"
    referrerpolicy="no-referrer"
    :srcdoc="document"
    title="Visualization"
  />
</template>

<script>
import { TOKENS_CSS, FRAME_CSP, demoSafeRows, demoSafeInputs, jsonForScript } from './widgets/frameTokens';

const D3_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/d3/7.9.0/d3.min.js';

export default {
  name: 'D3Insight',
  props: {
    code: { type: String, required: true },
    rows: { type: Array, default: () => [] },
    inputs: { type: Object, default: null },
    minHeight: { type: Number, default: 320 },
    fixedHeight: { type: Number, default: 0 },
  },
  data() {
    return { height: this.fixedHeight || this.minHeight };
  },
  computed: {
    document() {
      const inputs = demoSafeInputs(this.inputs || {});
      const names = Object.keys(inputs);
      const rows = this.rows && this.rows.length ? demoSafeRows(this.rows) : (names.length ? inputs[names[0]] : []);
      return `<!doctype html><html><head>
<meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${FRAME_CSP}">
<style>
  ${TOKENS_CSS}
  html, body { margin: 0; max-width: 100%; overflow-x: hidden; }
  body { font: 13px -apple-system, system-ui, sans-serif; color: #38352f; background: #fff; }
  #chart { width: 100%; max-width: 100%; }
  #chart svg { max-width: 100%; height: auto; display: block; }
  .err { padding: 12px; color: #c23a28; font-family: 'IBM Plex Mono', monospace; font-size: 12px; white-space: pre-wrap; }
</style>
<script src="${D3_SRC}"><\/script>
</head><body>
<div id="chart"></div>
<script>
(function () {
  var data = ${jsonForScript(rows)};
  var inputs = ${jsonForScript(inputs)};
  var el = document.getElementById('chart');
  var width = el.clientWidth;
  var post = function () {
    parent.postMessage({ d3insight: 'height', value: document.documentElement.scrollHeight }, '*');
  };
  var fit = function () {
    el.querySelectorAll('svg').forEach(function (svg) {
      if (!svg.getAttribute('viewBox')) {
        var w = parseFloat(svg.getAttribute('width'));
        var h = parseFloat(svg.getAttribute('height'));
        if (w > 0 && h > 0) svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
      }
      svg.removeAttribute('width');
      svg.removeAttribute('height');
      svg.setAttribute('preserveAspectRatio', 'xMinYMin meet');
    });
  };
  try {
    ${this.code}
  } catch (e) {
    el.innerHTML = '<div class="err">' + (e && e.message ? e.message : String(e)) + '<\\/div>';
  }
  fit();
  new ResizeObserver(post).observe(document.documentElement);
  post();
})();
<\/script>
</body></html>`;
    },
  },
  watch: {
    fixedHeight(value) {
      if (value) this.height = value;
    },
  },
  mounted() {
    window.addEventListener('message', this.onMessage);
  },
  beforeUnmount() {
    window.removeEventListener('message', this.onMessage);
  },
  methods: {
    onMessage(event) {
      const frame = this.$refs.frame;
      if (!frame || event.source !== frame.contentWindow) return;
      if (this.fixedHeight) return;
      if ((event.data || {}).d3insight === 'height') {
        this.height = Math.max(this.minHeight, Number(event.data.value) || this.minHeight);
      }
    },
  },
};
</script>
